import type {
  Inventory,
  Suggestion,
  AccessControl,
  Access,
  Identity,
  InventoryMe,
} from '@/types';
import { isEqual } from 'lodash/fp';
import { api } from './api';

const inventoryApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getInventory: builder.query<Inventory, number>({
      query: (id) => `inventories/${id}`,
    }),
    getInventorySuggestions: builder.query<Suggestion[], number>({
      query: (id) => `inventories/${id}/suggestions`,
    }),
    getInventoryMe: builder.query<InventoryMe, number>({
      query: (id) => `inventories/${id}/me`,
    }),
    getAccessControlCandidates: builder.query<Identity[], number>({
      query: (id) => `inventories/${id}/access-control-candidates`,
    }),
    getAccessControls: builder.query<AccessControl[], number>({
      query: (id) => `inventories/${id}/access-controls`,
    }),
    removeAuthorization: builder.mutation<{}, RemoveAuthorization>({
      query: ({ inventoryId, identity }) => ({
        url: `inventories/${inventoryId}/access-controls/${identity.type}/${identity.id}`,
        method: 'DELETE',
        body: {},
      }),
      onQueryStarted: async ({ inventoryId, identity }, { dispatch }) => {
        dispatch(
          inventoryApi.util.updateQueryData(
            'getAccessControls',
            inventoryId,
            (current) => {
              const index = current.findIndex((a) =>
                isEqual(a.identity, identity),
              );
              current.splice(index, 1);
            },
          ),
        );
      },
    }),
    updateAuthorization: builder.mutation<{}, UpdateAuthorization>({
      query: ({ inventoryId, identity, access }) => ({
        url: `inventories/${inventoryId}/access-controls/${identity.type}/${identity.id}`,
        method: 'PATCH',
        body: {
          access,
        },
      }),
      onQueryStarted: async (
        { inventoryId, identity, access },
        { dispatch },
      ) => {
        dispatch(
          inventoryApi.util.updateQueryData(
            'getAccessControls',
            inventoryId,
            (current) => {
              const v = current.find((a) => isEqual(a.identity, identity));
              if (v) {
                v.access = access;
              }
            },
          ),
        );
      },
    }),
    newAuthorization: builder.mutation<{}, UpdateAuthorization>({
      query: ({ inventoryId, identity, access }) => ({
        url: `inventories/${inventoryId}/access-controls/${identity.type}/${identity.id}`,
        method: 'PATCH',
        body: {
          access,
        },
      }),
      onQueryStarted: async (
        { inventoryId, identity, access },
        { dispatch },
      ) => {
        dispatch(
          inventoryApi.util.updateQueryData(
            'getAccessControls',
            inventoryId,
            (current) => {
              current.push({ identity, access });
            },
          ),
        );
      },
    }),
    setDefaultInventory: builder.mutation<{}, number>({
      query: (id) => ({
        url: `inventories/${id}/default`,
        method: 'POST',
        body: {},
      }),
      onQueryStarted: async (id, { dispatch }) => {
        dispatch(
          inventoryApi.util.updateQueryData(
            'getDefaultInventory',
            undefined,
            (current) => {
              current.id = id;
            },
          ),
        );
      },
    }),
    getDefaultInventory: builder.query<{ id: number }, void>({
      query: (id) => `inventories/${id}/default`,
    }),
  }),
});

export type RemoveAuthorization = {
  inventoryId: number;
  identity: Identity;
};

export type UpdateAuthorization = {
  inventoryId: number;
  identity: Identity;
  access: Access;
};

export const {
  useGetInventoryQuery,
  useGetInventorySuggestionsQuery,
  useGetInventoryMeQuery,
  useGetAccessControlsQuery,
  useGetAccessControlCandidatesQuery,
  useRemoveAuthorizationMutation,
  useUpdateAuthorizationMutation,
  useNewAuthorizationMutation,
  useSetDefaultInventoryMutation,
  useGetDefaultInventoryQuery,
} = inventoryApi;
